import _ from 'lodash';
import React, { useCallback, useState, useMemo, useEffect } from 'react';
import ReactTooltip from 'react-tooltip';
// TODO: Import @material-ui from design-system folder
// eslint-disable-next-line restrict-imports/restrict-imports
import { makeStyles } from '@material-ui/core/styles';
import Kbd from 'design-system/Kbd';
import { AuditResult, BenchmarkIIAuditResult, SpeedAuditVersion } from '@scale/shared/defaults';
import { InputText } from 'design-system/Form';
import { H2 } from 'design-system';
import { resultColors, SpeedAuditResult, SpotterAuditResult } from './types';
// TODO: Migrate module so that you don’t import from the corp project in a global module
// eslint-disable-next-line restrict-imports/restrict-imports
import InstructionItemsEditor from 'corp/components/Projects/InstructionItemsEditor';
import { IInstructionItemJson } from '@scale/shared/types/instructionitem';
import { Button } from 'design-system/Button';
import InstructionItemSummary from 'react-scale/components/benchmarks/InstructionItemSummary';
import {
  InstrItemTags,
  modifyAnnotationInstrItemTags,
  modifyTaskInstrItems,
  instrItemSummaryGenerator,
  ModifyInstrItemTagsMode,
  isAnnotationIsCurrentlyTagged,
} from './modifyInstructionItemTags';
import { getFriendlyAnnotationId } from '@scale/shared/annotation';
import { AuditType } from '@scale/shared/types/corp/attemptAudit';
import DropZone from 'react-scale/corp/components/DropZone';
import SAlert from 'react-s-alert';
import apiFetch from '@scale/frontend-lib/api-fetch';
import uuid from 'uuid';
import {
  AWS_REGION,
  AWS_SUFFIX,
  STATIC_ASSETS_BUCKET,
  IS_FEDERAL,
} from '@scale/frontend-lib/constants';
import { logCorpEvent } from '@scale/frontend-lib/analytics/log-corp-event';
import { Event } from '@scale/shared/types/events';
import {
  ElementClickedType,
  GeneralElementClicked,
} from '@scale/shared/types/events/GeneralElementClicked';
import { speedAuditEventPrefix } from 'react-scale/components/audits/SpeedAudit/utils';

interface SpeedAuditFormProps {
  result: SpeedAuditResult;
  updateResult: (
    speedAuditResult: SpeedAuditResult,
    newSpeedAuditResultData: Partial<SpeedAuditResult>,
  ) => void;
  auditBatchId?: string;
  projectId: string;
  projectInstrItems: IInstructionItemJson[];
  selectedAnnotation?: string;
  auditType?: AuditType;
  isSBQ?: boolean;
  speedAuditVersion?: SpeedAuditVersion;
}

const REMOTASKS_STATIC_CDN = IS_FEDERAL
  ? `https://${STATIC_ASSETS_BUCKET}.s3-${AWS_REGION}.${AWS_SUFFIX}`
  : 'https://static.remotasks.com';

function SpeedAuditForm({
  auditType,
  updateResult,
  isSBQ,
  projectId,
  projectInstrItems,
  result,
  selectedAnnotation,
  speedAuditVersion,
}: SpeedAuditFormProps) {
  const styles = useSpeedAuditFormStyles();

  // Currently selected ids of instructionitems
  const [selectedInstrItems, setInstrItems] = useState<string[]>([]);
  const canSubmitInstrItemTag: boolean = !_.isEmpty(selectedInstrItems) && !!selectedInstrItems[0];

  const onChangeResult = useCallback(
    (auditResult: AuditResult | SpotterAuditResult): React.ChangeEventHandler<HTMLInputElement> =>
      async () => {
        let resultValue: AuditResult;
        if (auditResult === SpotterAuditResult.Agree) {
          resultValue = AuditResult.Perfect;
        } else if (auditResult === SpotterAuditResult.Disagree) {
          resultValue = AuditResult.Terrible;
        } else {
          resultValue = auditResult;
        }

        updateResult(result, { result: resultValue });
        logCorpEvent(Event.GeneralElementClicked, {
          name: `${speedAuditEventPrefix(speedAuditVersion)} > Grade Selected`,
          type: ElementClickedType.Link,
        } as GeneralElementClicked);
      },
    [speedAuditVersion, updateResult, result],
  );

  const [comments, setComments] = useState(result.comments || '');
  useEffect(() => setComments(result.comments || ''), [result.comments]);

  const debouncedUpdateResultComments = useMemo(
    () =>
      _.debounce((newComments: string) => {
        updateResult(result, { comments: newComments });
      }, 3000),
    [result, updateResult],
  );
  const onChangeComments = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newComments = event.target.value;
      setComments(newComments);
      debouncedUpdateResultComments(newComments);
    },
    [debouncedUpdateResultComments],
  );

  const [file, setFile] = useState<File | undefined>();
  const [uploadingFile, setUploadingFile] = useState(false);

  useEffect(() => {
    async function uploadFiles(file: File) {
      setUploadingFile(true);
      SAlert.warning('Uploading file...');
      const fileName = `${uuid.v4()}_audit`;
      const body = {
        folderName: 'uploads',
        fileName,
        overwrite: true,
      };
      try {
        const { url, fields } = await apiFetch(null, 'corp-api/upload_static_asset_to_s3', {
          method: 'POST',
          body,
        });
        const formData = new FormData();
        for (const [k, v] of Object.entries(fields)) {
          formData.append(k, v as string);
        }
        formData.append('file', file);
        await fetch(url, { method: 'POST', body: formData });
        const location = `${REMOTASKS_STATIC_CDN}/uploads/${fileName}`;
        updateResult(result, { media: location });
        setFile(undefined);
      } catch (error) {
        SAlert.error('Failed to upload file.');
      }
      setUploadingFile(false);
    }
    if (file && !uploadingFile) {
      uploadFiles(file);
    }
  }, [file, uploadingFile, result, updateResult]);

  const stopKeyPress = useCallback((event: React.KeyboardEvent<HTMLInputElement>): void => {
    event.stopPropagation();
  }, []);

  const saveInstrItemTags = useCallback(
    async (tags: InstrItemTags) => {
      updateResult(result, {
        taskInstructionItems: tags.taskInstructionItems,
        perAnnotationInstructionItems: tags.perAnnotationInstructionItems,
      });
    },
    [updateResult, result],
  );

  const onModifyInstrItemTags = useCallback(
    async (mode: ModifyInstrItemTagsMode) => {
      const newInstrItemTags: InstrItemTags = {
        taskInstructionItems: result.taskInstructionItems ?? [],
        perAnnotationInstructionItems: result.perAnnotationInstructionItems ?? [],
      };
      // Case: Annotation-tagged instruction items.
      if (selectedAnnotation) {
        newInstrItemTags.perAnnotationInstructionItems = modifyAnnotationInstrItemTags(
          mode,
          newInstrItemTags.perAnnotationInstructionItems,
          selectedInstrItems,
          selectedAnnotation,
        );
      } else {
        // Case: Task-level instruction items.
        newInstrItemTags.taskInstructionItems = modifyTaskInstrItems(
          mode,
          newInstrItemTags.taskInstructionItems,
          selectedInstrItems,
        );
      }
      // Save the data.
      await saveInstrItemTags(newInstrItemTags);
      setInstrItems([]);
    },
    [
      result.perAnnotationInstructionItems,
      result.taskInstructionItems,
      saveInstrItemTags,
      selectedAnnotation,
      selectedInstrItems,
    ],
  );

  const memoizedInstrItemSummaryGenerator = _.memoize(instrItemSummaryGenerator);

  const isSpotterAudit = auditType === AuditType.SPOTTER_AUDIT;
  const isBenchmarkThresholdAudit = auditType === AuditType.BENCHMARK_II_THRESHOLDS;

  const validResults = useMemo(
    () =>
      isBenchmarkThresholdAudit
        ? BenchmarkIIAuditResult
        : isSpotterAudit
        ? [SpotterAuditResult.Agree, SpotterAuditResult.Disagree]
        : _.values(AuditResult),
    [isBenchmarkThresholdAudit, isSpotterAudit],
  );

  return (
    <div className={styles.form}>
      <div className={styles['speed-audit-form__user-input-container']}>
        {isSBQ !== undefined && <H2>Spotter Decision: {isSBQ ? 'REJECT' : 'SEND'}</H2>}
        <div className={styles['speed-audit-form__input']}>
          <div className={styles.legend}>Grade</div>
          {(validResults as (SpotterAuditResult | AuditResult)[]).map(
            (auditResult: AuditResult | SpotterAuditResult) => (
              <Button
                key={auditResult}
                className={styles.auditResultButton}
                style={{
                  backgroundColor: resultColors[auditResult],
                  opacity: isResultChecked(result.result, auditResult) ? 1 : 0.3,
                }}
                onClick={onChangeResult(auditResult)}
              >
                {auditResult}
              </Button>
            ),
          )}
          <div className={styles.note}>
            <div className={styles.legend}>Comments</div>
            <InputText
              placeholder="Write Comments"
              full
              size="md"
              key={result.attemptAuditId}
              value={comments}
              onChange={onChangeComments}
              onKeyDown={stopKeyPress}
            />
          </div>
          <div className={styles.upload}>
            <DropZone
              width={100}
              height={70}
              handleDrop={file => {
                setFile(file[0]);
                logCorpEvent(Event.GeneralElementClicked, {
                  name: `${speedAuditEventPrefix(speedAuditVersion)} > Upload Image`,
                  type: ElementClickedType.Button,
                } as GeneralElementClicked);
              }}
              dropzoneParams={{ multiple: false }}
              overrideLabel={'Upload image'}
              tooltipLabel={`Click or drag files to upload an image. Uploaded image for Object ${result.objectId} will be displayed in the downloaded CSV.`}
            />
          </div>
        </div>
      </div>
      <div className={styles.note}>
        <div style={{ display: 'flex', flexDirection: 'row' }}>
          <div className={styles.instrItemSelector}>
            Instruction Item Tagging <Kbd>q</Kbd> to view
            <InstructionItemsEditor
              instructionItems={_.map(
                selectedInstrItems,
                id => _.find(projectInstrItems, instrItem => instrItem._id === id) ?? {},
              )}
              isCreatable={false}
              handleEdit={newItems => {
                setInstrItems(_.map(newItems, instrItemJson => instrItemJson._id));
              }}
              query={{ benchmarkProject: projectId }}
            />
          </div>
          <div className={styles.annotSelector}>
            <div className={styles.legend} data-tip={'Select an Annotation in the Labeler'}>
              Currently Selected Annotation:
            </div>
            <code>
              {selectedAnnotation ? getFriendlyAnnotationId(selectedAnnotation) : 'Task-Level'}
            </code>
            {selectedAnnotation &&
              isAnnotationIsCurrentlyTagged(
                selectedAnnotation,
                result.perAnnotationInstructionItems,
              ) && <small>Currently Tagged</small>}
            {canSubmitInstrItemTag && (
              <>
                <Button
                  style={{ margin: '5px', width: '150px' }}
                  // why does passing fullWidth in not work? Manually setting width here.
                  fullWidth={false}
                  onClick={() => onModifyInstrItemTags(ModifyInstrItemTagsMode.Add)}
                >
                  Add Tags
                </Button>
                <Button
                  style={{ margin: '5px', width: '150px' }}
                  fullWidth={false}
                  onClick={() => onModifyInstrItemTags(ModifyInstrItemTagsMode.Delete)}
                >
                  Remove Tags
                </Button>
              </>
            )}
            <InstructionItemSummary
              shortcutKey={'q'}
              data={memoizedInstrItemSummaryGenerator(
                {
                  taskInstructionItems: result.taskInstructionItems ?? [],
                  perAnnotationInstructionItems: result.perAnnotationInstructionItems ?? [],
                },
                projectInstrItems,
              )}
            />
          </div>
        </div>
      </div>
      <ReactTooltip />
    </div>
  );
}

const useSpeedAuditFormStyles = makeStyles({
  instrItemSelector: {
    padding: '8px',
    width: '300px',
    overflow: 'visible',
  },
  annotSelector: {
    display: 'flex',
    flexDirection: 'column',
    width: '300px',
  },
  form: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    overflow: 'visible',
    paddingLeft: '24px',
    paddingRight: '24px',
  },
  'speed-audit-form__user-input-container': {
    display: 'flex',
    flexDirection: 'column',
  },
  'speed-audit-form__input': {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  legend: {
    fontWeight: 'bold',
    marginRight: 4,
  },
  auditResultButton: {
    marginLeft: 4,
  },
  note: {
    marginLeft: '24px',
    width: '600px',
    height: 'max-content',
  },
  upload: {
    marginLeft: '24px',
  },
});

const isResultChecked = (
  result: AuditResult | undefined,
  currentValue: SpotterAuditResult | AuditResult,
) => {
  return (
    result === currentValue ||
    (result === AuditResult.Perfect && currentValue === SpotterAuditResult.Agree) ||
    (result === AuditResult.Terrible && currentValue === SpotterAuditResult.Disagree)
  );
};

export default React.memo(SpeedAuditForm);
